import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Lock, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "August 5, 2026";

const Section = ({ n, title, children }) => (
  <section className="mt-8" data-testid={`privacy-section-${n}`}>
    <h2 className="font-heading text-xl tracking-tight" style={{ fontWeight: 700 }}>{n}. {title}</h2>
    <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-foreground/80">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-12" data-testid="privacy-page">
        <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <div className="mt-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Lock className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-4xl tracking-tight" style={{ fontWeight: 900 }}>Privacy Policy</h1>
            <p className="mt-1 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>

        <p className="mt-6 text-[15px] leading-relaxed text-foreground/80">
          This Privacy Policy explains how BogoShare ("we", "us") collects, uses, and protects your personal
          information when you use our Platform. It should be read together with our{" "}
          <Link to="/terms" className="font-semibold text-primary hover:underline" data-testid="privacy-terms-link">Terms of Service</Link>.
        </p>

        <Section n={1} title="Information We Collect">
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Account information</b> — your name, email address, and password (stored only as a secure hash).</li>
            <li><b>Verification &amp; security</b> — one-time codes, and limited device information (browser type and approximate location derived from your IP) for trusted-device and security-alert features.</li>
            <li><b>Transaction information</b> — deals you post or join, agreed fees, payout details you provide, and records needed to process payments and payouts.</li>
            <li><b>Support &amp; communications</b> — messages you send us through the contact form or email.</li>
            <li><b>Usage information</b> — basic logs and technical data generated as you use the Platform.</li>
          </ul>
        </Section>

        <Section n={2} title="How We Use It">
          <p>
            We use your information to operate the Platform: to create and secure your account, verify your email,
            match and settle deals, process payments and payouts, prevent fraud and abuse, respond to your messages,
            send service-related emails, and comply with our legal obligations.
          </p>
        </Section>

        <Section n={3} title="Sharing">
          <p>
            We do not sell your personal information. We share it only with service providers that help us run the
            Platform — for example our payment processor, our payout provider, and our email delivery provider — and only
            as needed to provide those services, or where required by law or to protect our rights. Other members see
            only the limited information necessary to complete a deal with you.
          </p>
        </Section>

        <Section n={4} title="Retention & Security">
          <p>
            We keep your information for as long as your account is active and as needed for legal, accounting, and
            fraud-prevention purposes. We use reasonable technical and organizational safeguards — including hashed
            passwords, encrypted transport, and short-lived verification codes — to protect your information. No method
            of transmission or storage is perfectly secure.
          </p>
        </Section>

        <Section n={5} title="Your Choices & Rights">
          <p>
            You may access or update your account information, manage your trusted devices from the Security page, and
            request deletion of your account by contacting us. Subject to applicable Canadian privacy laws, you may have
            rights to access, correct, or withdraw consent to certain uses of your information.
          </p>
        </Section>

        <Section n={6} title="Contact">
          <p>
            For privacy questions or requests, email{" "}
            <a href="mailto:admin@bogoshare.com" className="font-semibold text-primary hover:underline" data-testid="privacy-contact-email">admin@bogoshare.com</a>.
          </p>
        </Section>

        <div className="mt-10 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="privacy-legal-notice">
          <b>Please note:</b> This document is a general template and is not legal advice. We recommend a qualified
          lawyer review it to ensure it meets PIPEDA and any provincial privacy requirements that apply to you.
        </div>
      </main>
    </div>
  );
}
